import styled from '@emotion/styled'
import { H4, Text } from 'library/atoms/Typography'
import useTranslation from 'next-translate/useTranslation'
import { useRouter } from 'next/router'
import {
  DEFAULT_SIDEBAR_PANEL,
  SidebarNavListItem,
  SIDEBAR_GHOST_ITEM
} from 'enums/sidebar'
import Button from 'library/atoms/Button'
import Link from 'next/link'
import Icon from 'shared/library/atoms/Icon'
import formatToSnakeCase from 'shared/helpers/formatToSnakeCase'
import { SidebarContextType } from 'providers/SidebarProvider'
import { useSidebar } from 'state/useSidebar'

type PanelPositionVariants = 'previous' | 'current' | 'next'
type PanelPosition = { panelPosition?: PanelPositionVariants }
type Links = SidebarNavListItem[]

type SidebarNavListProps = {
  title: string
  links?: Links
}

const SidebarNavList = ({ title, links }: SidebarNavListProps) => {
  const { t } = useTranslation('common')
  const { visibleNavList, returnToPreviousNavList } = useSidebar()

  const { current, previous } = determineNavListPosition({
    visibleNavList,
    name: title
  })

  const panelPosition: PanelPositionVariants = previous
    ? 'previous'
    : current
      ? 'current'
      : 'next'

  const isInitialNav = title === DEFAULT_SIDEBAR_PANEL

  return (
    <StyledSidebarNavList
      panelPosition={panelPosition}
      role="region"
      /**
       * It is only aria-hidden when it is "next" because if it is done for "previous" too
       * the parent with "previous" would make the children hidden as well.
       * */
      aria-hidden={panelPosition === 'next'}
      id={generateAriaContent(title)}
      aria-labelledby={generateAriaTitle(title)}
    >
      {!isInitialNav && (
        <StyledNavItem variant="back-button">
          <StyledBackButton
            variant="secondary"
            onClick={() => returnToPreviousNavList()}
            {...determineTabIndex(current)}
          >
            <Icon name="arrow-left" color="currentColor" size={14} mr={2} />
            {t('back')}
          </StyledBackButton>
        </StyledNavItem>
      )}
      {!isInitialNav && <H4 mb={3}>{title}</H4>}
      {links?.map((link, index) => (
        <NavItem
          // There are cases the name isn't unique
          key={`${link.name} ${index}`}
          listName={title}
          {...link}
          belongsToSubnav={!isInitialNav}
        />
      ))}
    </StyledSidebarNavList>
  )
}

export default SidebarNavList

type BelongsToSubnav = { belongsToSubnav: boolean; isCurrent?: boolean }
type NavItemProps = SidebarNavListItem &
  Pick<NavItemContentProps, 'icon'> &
  BelongsToSubnav & {
    /**
     * The name of the list to which this menu item belongs.
     */
    listName: string
  }

export const NavItem = ({
  url,
  userId,
  name,
  listName,
  icon,
  kind,
  collection,
  belongsToSubnav
}: NavItemProps) => {
  const { asPath } = useRouter()
  const { visibleNavList, updateVisibleNavList } = useSidebar()

  const { current } = determineNavListPosition({
    visibleNavList,
    name
  })

  const { current: parentIsCurrent } = determineNavListPosition({
    visibleNavList,
    name: listName
  })

  const COMMON_PROPS = {
    navItemButton: {
      belongsToSubnav,
      ['aria-current']: asPath === url,
      ...determineTabIndex(parentIsCurrent)
    },
    navItemContent: {
      name,
      icon,
      kind,
      subLinks: collection,
      belongsToSubnav
    }
  }

  // Cooming soon features
  if (kind === SIDEBAR_GHOST_ITEM) {
    return (
      <StyledNavItem key={name}>
        <StyledNavItemButton as="div" {...COMMON_PROPS.navItemButton}>
          <NavItemContent {...COMMON_PROPS.navItemContent} />
        </StyledNavItemButton>
      </StyledNavItem>
    )
  }

  /**
   * Admin share link
   *
   * Admn share link is an edge case since it doesn't return a link but is used for setting something to the clipboard
   * */
  if (url && userId) {
    return (
      <StyledNavItem key={name}>
        <StyledNavItemButton
          onClick={() => {
            copyAdminUrl(userId)
          }}
          {...COMMON_PROPS.navItemButton}
        >
          <NavItemContent {...COMMON_PROPS.navItemContent} />
        </StyledNavItemButton>
      </StyledNavItem>
    )
  }

  // Links
  if (url) {
    const href = url?.includes('https')
      ? url
      : /**
         * Use slice to remove extra forward slash at the ending of BUSINESS_ROOT
         * because this "url" already has a slash at the beginning.
         */
        `${process.env.NEXT_PUBLIC_EOLA_BUSINESS_ROOT?.slice(0, -1)}${url}`

    if (COMMON_PROPS.navItemContent.name === 'Log out') {
      return (
        <StyledNavItem key={name}>
          <form action={href} method="post">
            <input type="hidden" name="_method" value="DELETE" />
            <StyledNavItemButton
              {...COMMON_PROPS.navItemButton}
              isCurrent={isCurrentSidebarLink(url, asPath)}
            >
              <NavItemContent {...COMMON_PROPS.navItemContent} />
            </StyledNavItemButton>
          </form>
        </StyledNavItem>
      )
    }

    return (
      <StyledNavItem key={name}>
        <Link href={href} passHref>
          <StyledNavItemButton
            {...COMMON_PROPS.navItemButton}
            isCurrent={isCurrentSidebarLink(url, asPath)}
          >
            <NavItemContent {...COMMON_PROPS.navItemContent} />
          </StyledNavItemButton>
        </Link>
      </StyledNavItem>
    )
  }

  // Buttons
  return (
    <StyledNavItem key={name}>
      <StyledNavItemButton
        onClick={() => {
          updateVisibleNavList(name)
        }}
        {...COMMON_PROPS.navItemButton}
        aria-expanded={current}
        aria-selected={false}
        aria-controls={generateAriaContent(name)}
        id={generateAriaTitle(name)}
      >
        <NavItemContent {...COMMON_PROPS.navItemContent} />
      </StyledNavItemButton>
      {collection?.length && (
        /**
         * The SidebarNavList is wrapped into StyledSubnav because it helps to reset the position
         * when the NavItem moves to the left to show this subnav.
         *
         * In other words, StyledSubnav has a position relative to prevent  SidebarNavList from
         * using the position relative from a higher parent element that has been moved to the
         * left to show the following subnav.
         * */
        <StyledSubnav current={current}>
          {/**
           * The following component is a recursive function, the SidebarNavList is recursively
           * reusing its logic to generate the sub navigations.
           * */}
          <SidebarNavList title={name} links={collection} />
        </StyledSubnav>
      )}
    </StyledNavItem>
  )
}

/**
 * Getting the end of the url as otherwise any links that are contained within
 * the current pages url are marked as active.
 * e.g. /widgets/settings would mark "My widgets" (/widgets) and "Widget settings" as active
 *
 * TODO: #7148 Fix sidebar navigation to show correct active links for 'sub' pages correctly
 * (e.g. /widgets/acw/edit = "My widgets")
 */
export const isCurrentSidebarLink = (
  sidebarLinkPath: string,
  currentPath: string
) => {
  const sidebarLinkPage = sidebarLinkPath.split('/').slice(-1)[0]
  const currentPage = currentPath?.split('/').slice(-1)[0]

  return (
    sidebarLinkPath.includes(`${currentPath}`) &&
    sidebarLinkPage === currentPage
  )
}

const copyAdminUrl = (userId: string) => {
  const url = new URL(window.location.href)
  const params = new URLSearchParams(url.search)
  params.append('admin_share', userId)
  const urlToCopy = window.location.href.split('?')[0] + '?' + params
  navigator.clipboard.writeText(urlToCopy).then(
    () => {
      alert(
        'The link to sign in with this page has been copied to your clipboard.'
      )
    },
    () => {
      alert('Copying to the clipboard failed.')
    }
  )
}

const determineNavListPosition = ({
  visibleNavList,
  name
}: Pick<SidebarContextType, 'visibleNavList'> & {
  name?: string
}) => {
  const current = visibleNavList?.current === name
  const previous = visibleNavList?.previous?.some(item => item === name)

  return {
    current,
    previous
  }
}

const determineTabIndex = (current: boolean) => ({ tabIndex: current ? 0 : -1 })
const generateAriaContent = (name: string) =>
  `${formatToSnakeCase(name)}_content`
const generateAriaTitle = (name: string) => `${formatToSnakeCase(name)}_title`

type NavItemContentProps = Pick<SidebarNavListItem, 'name' | 'kind'> &
  BelongsToSubnav & {
    icon?: string
    subLinks?: Links
  }

const NavItemContent = ({
  name,
  icon,
  kind,
  subLinks,
  belongsToSubnav
}: NavItemContentProps) => {
  const { t } = useTranslation('common')
  return (
    <>
      {!belongsToSubnav && (
        <StyledLeftIcon
          name={icon ?? 'placeholder'}
          size={32}
          color="currentColor"
        />
      )}
      {name}
      {kind === SIDEBAR_GHOST_ITEM && (
        <StyledNavItemSoon as="span" fontStyle="p5">
          {t('soon')}
        </StyledNavItemSoon>
      )}
      {subLinks?.length && (
        <StyledRightIcon name="arrow-right" size={12} color="currentColor" />
      )}
    </>
  )
}

const StyledSidebarNavList = styled.ul<PanelPosition>(
  ({ theme, panelPosition = 'next' }) => {
    const positions = {
      previous: {
        left: '-100%',
        zIndex: 0
      },
      current: {
        left: 0,
        zIndex: 1
      },
      next: {
        left: '100%',
        zIndex: 0
      }
    }

    return {
      position: 'absolute',
      top: 0,
      left: positions[panelPosition].left,
      zIndex: positions[panelPosition].zIndex,
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      width: '100%',
      listStyle: 'none',
      padding: theme.mixins.pxSpread([0, theme.space[5]]),
      margin: 'auto 0',
      gap: theme.space[2],
      transition: `${theme.transitionTime[1]}s ease-in-out`
    }
  }
)

const StyledNavItem = styled.li<{ variant?: 'back-button' }>(
  () => ({
    padding: 0,
    margin: 0,
    width: '100%',
    '&:last-of-type': {
      marginBottom: 'auto'
    },
    a: {
      textDecoration: 'none'
    }
  }),
  ({ variant }) =>
    variant
      ? {
          marginBottom: 'auto'
        }
      : {
          '&:first-of-type': {
            marginTop: 'auto'
          }
        }
)

const StyledSubnav = styled.div<{ current: boolean }>(({ theme, current }) => ({
  position: 'absolute',
  top: 0,
  zIndex: current ? 1 : 0,
  left: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  height: '100%',
  width: '100%',
  background: theme.colors.background.primary,
  transition: `${theme.transitionTime[1]}s ease-in-out`
}))

const StyledBackButton = styled(Button)(({ theme }) => ({
  width: '100%',
  padding: theme.mixins.pxSpread([theme.space[5], 0]),
  span: {
    justifyContent: 'start'
  }
}))

const StyledNavItemButton = styled.button<BelongsToSubnav>(
  ({ theme, isCurrent }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    textAlign: 'left',
    width: '100%',
    color: theme.colors.text.secondary,
    textDecoration: isCurrent ? 'underline' : 'none',
    margin: 0,
    border: 'none',
    background: 'none',
    transition: `${theme.transitionTime[1]}s ease`,
    /**
     * The item will be rendered as a div if it is related to a ghost element which is how
     * the sidebar describes future features.
     */
    '&:not(div)': {
      cursor: 'pointer',
      '&[aria-current="true"]': {
        textDecoration: 'underline'
      },
      '&:hover': {
        textDecoration: 'underline'
      },
      '&:hover, &:focus-visible': {
        [`${StyledLeftIcon}`]: {
          background: theme.colors.white
        },
        [`${StyledRightIcon}`]: {
          opacity: 1,
          marginRight: 0
        }
      }
    }
  }),
  ({ theme, belongsToSubnav }) =>
    belongsToSubnav
      ? {
          ...theme.fontStyle.p4,
          padding: theme.mixins.pxSpread([
            theme.space[1],
            theme.space[2],
            theme.space[1],
            theme.space[3]
          ])
        }
      : {
          ...theme.fontStyle.h4,
          padding: theme.mixins.pxSpread([theme.space[2], 0])
        }
)

const StyledNavItemSoon = styled(Text)(({ theme }) => ({
  padding: theme.mixins.pxSpread([0, theme.space[2]]),
  border: theme.border,
  borderRadius: theme.radii[2],
  textTransform: 'uppercase',
  fontStyle: 'italic',
  marginLeft: theme.space[2]
}))

const StyledLeftIcon = styled(Icon)(({ theme }) => ({
  marginRight: theme.space[2],
  padding: theme.space[2],
  borderRadius: theme.radii[3],
  transition: `${theme.transitionTime[1]}s ease`
}))

const StyledRightIcon = styled(Icon)(({ theme }) => ({
  opacity: 0,
  marginRight: theme.space[2],
  marginLeft: 'auto',
  transition: `${theme.transitionTime[1]}s ease`
}))
